import { Button, Card } from '@shared/components/ui';
import { useDialog } from '@shared/hooks/useDialog';
import { Plus, Search, Settings, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BackupConfigItem } from '../components/BackupConfigItem';
import { BackupConfigWizard } from '../components/BackupConfigWizard';

interface Agent {
  id: string;
  name: string;
  status: string;
}

interface BackupConfig {
  id: string;
  name: string;
  description?: string;
  type: string;
  agent_id: string;
  agent_name?: string;
  storage_destination_id?: string;
  storage_destination_name?: string;
  config?: {
    // FILES type
    paths?: string[];
    // POSTGRESQL type - flat structure
    host?: string;
    port?: number;
    database?: string;
    databases?: string[];
    username?: string;
    use_pg_dump_all?: boolean;
    backup_globals_only?: boolean;
  };
  retention_policy?: unknown;
  is_enabled: boolean;
  run_missing_jobs_on_startup: boolean;
  created_at: string;
  last_job_status?: string;
  last_job_completed_at?: string;
}

export default function BackupConfigs() {
  const { t, i18n } = useTranslation('backups');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id?: string }>();
  const routeState = location.state as {
    openCreateWizard?: boolean;
    backupConfigAgentId?: string;
  } | null;
  const { showSuccess, showError, showConfirm } = useDialog();
  const [configs, setConfigs] = useState<BackupConfig[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterAgent, setFilterAgent] = useState<string>(
    () => searchParams.get('agent') || routeState?.backupConfigAgentId || 'all'
  );
  const [showWizard, setShowWizard] = useState(false);
  const [editConfigId, setEditConfigId] = useState<string | undefined>(undefined);

  const getToken = useCallback(() => localStorage.getItem('auth_token'), []);

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const res = await fetch('/api/v1/backups/configs', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      // A non-2xx response (rate limit, auth, server error) carries an error
      // object, not a list. Storing it would break `configs.filter` below.
      if (!res.ok) {
        setConfigs([]);
        setLoadError(true);
        return;
      }
      const data = await res.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/agents', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      setAgents(Array.isArray(data) ? data : []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }, [getToken]);

  // Handle edit route - open wizard when :id is present
  useEffect(() => {
    if (id && configs.length > 0) {
      setEditConfigId(id);
      setShowWizard(true);
    }
  }, [id, configs]);

  useEffect(() => {
    const requestedAgentId = routeState?.backupConfigAgentId;
    if (!routeState?.openCreateWizard && !requestedAgentId) return;

    if (routeState?.openCreateWizard) {
      setShowWizard(true);
    }

    const nextSearchParams = new URLSearchParams(location.search);
    if (requestedAgentId && !nextSearchParams.has('agent')) {
      nextSearchParams.set('agent', requestedAgentId);
    }
    const nextSearch = nextSearchParams.toString();

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true, state: null }
    );
  }, [location.hash, location.pathname, location.search, location.state, navigate, routeState]);

  useEffect(() => {
    fetchConfigs();
    fetchAgents();
  }, [fetchConfigs, fetchAgents]);

  const filteredConfigs = configs.filter((c) => {
    const matchesSearch =
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.agent_name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = filterType === 'all' || c.type === filterType;
    const matchesAgent = filterAgent === 'all' || c.agent_id === filterAgent;

    return matchesSearch && matchesType && matchesAgent;
  });
  const hasActiveFilters =
    searchQuery.trim().length > 0 || filterType !== 'all' || filterAgent !== 'all';

  const handleTrigger = async (config: BackupConfig) => {
    try {
      const res = await fetch(`/api/v1/backups/configs/${config.id}/trigger`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      if (res.ok) {
        await showSuccess(t('notifications.started'), t('notifications.backupStartedDesc'));
      } else {
        const data = await res.json();
        if (data.quota_exceeded) {
          const locale = i18n.resolvedLanguage || i18n.language;
          const usedGB = Number(data.used_gb).toLocaleString(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          const quotaGB = Number(data.quota_gb).toLocaleString(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });
          await showError(
            t('snapshots.quotaExceeded'),
            t('snapshots.quotaExceededMessage', { usedGB, quotaGB })
          );
        } else if (data.code === 'SUBSCRIPTION_REQUIRED') {
          await showError(
            t('notifications.subscriptionRequired'),
            `${data.error}\n\n${t('snapshots.subscribePrompt')}`
          );
        } else {
          await showError(
            t('notifications.backupFailed'),
            `${t('snapshots.backupTriggerFailed')} ${data.error || t('errors.unknown')}`
          );
        }
      }
    } catch {
      await showError(t('errors.networkError'), t('errors.networkRetry'));
    }
  };

  const handleDelete = async (config: BackupConfig) => {
    const confirmed = await showConfirm(
      t('modal.delete.title'),
      t('configs.deleteConfirmMessage', { name: config.name }),
      'warning'
    );
    if (!confirmed) return;
    await fetch(`/api/v1/backups/configs/${config.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    fetchConfigs();
  };

  const handleToggleEnable = async (config: BackupConfig) => {
    try {
      await fetch(`/api/v1/backups/configs/${config.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...config,
          is_enabled: !config.is_enabled,
        }),
      });
      fetchConfigs();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-300">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">
              {t('title')}
            </h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('subtitle')}
            </p>
          </div>
          <Button
            onClick={() => setShowWizard(true)}
            size="lg"
            className="shadow-brand-200 shrink-0 whitespace-nowrap"
          >
            <Plus className="w-5 h-5 mr-2" strokeWidth={3} /> {t('create.button')}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <Card className="lg:col-span-6 p-2 flex items-center bg-surface-card-light dark:bg-surface-card-dark">
            <Search className="ml-4 text-text-subtle-light dark:text-text-subtle-dark" size={20} />
            <input
              aria-label={t('search.placeholder')}
              type="text"
              placeholder={t('search.placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="!min-h-11 flex-1 px-4 py-3 bg-transparent border-none focus:ring-0 font-bold text-slate-600 dark:text-slate-300 outline-none"
            />
            {searchQuery && (
              <button
                type="button"
                aria-label={t('search.clear')}
                onClick={() => setSearchQuery('')}
                className="p-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-lg mr-2"
              >
                <X size={16} className="text-text-subtle-light dark:text-text-subtle-dark" />
              </button>
            )}
          </Card>
          <div className="grid grid-cols-2 items-center gap-2 lg:col-span-4">
            <select
              id="backup-config-filter-type"
              aria-label={t('filters.typeLabel')}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="!h-11 !min-h-11 px-3 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
            >
              <option value="all">{t('filters.allTypes')}</option>
              <option value="POSTGRESQL">PostgreSQL</option>
              <option value="FILES">{t('filters.type.files')}</option>
            </select>
            <select
              id="backup-config-filter-agent"
              aria-label={t('filters.agentLabel')}
              value={filterAgent}
              onChange={(e) => setFilterAgent(e.target.value)}
              className="!h-11 !min-h-11 px-3 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
            >
              <option value="all">{t('filters.allAgents')}</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <Card className="lg:col-span-2 p-4 flex items-center justify-center bg-accent-soft-light border-accent-text-light/20 dark:bg-accent-soft-dark dark:border-accent-text-dark/20">
            <div className="text-center">
              <p className="text-[10px] font-black uppercase tracking-widest text-brand-600 dark:text-brand-400 mb-1">
                {t('stats.totalActive')}
              </p>
              <p className="text-2xl font-black text-accent-text-light dark:text-brand-400 leading-none">
                {loadError || (loading && configs.length === 0)
                  ? '—'
                  : filteredConfigs.filter((c) => c.is_enabled).length}
              </p>
            </div>
          </Card>
        </div>

        {loading && configs.length === 0 ? (
          <div className="py-20 flex flex-col items-center justify-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
            <p className="text-text-subtle-light dark:text-text-subtle-dark font-black uppercase tracking-widest text-[10px]">
              {t('list.loading')}
            </p>
          </div>
        ) : loadError ? (
          <div role="alert" aria-live="assertive">
            <Card className="py-12 px-6 text-center border border-state-danger-light/20 dark:border-state-danger-dark/20 bg-state-danger-bg-light dark:bg-state-danger-bg-dark">
              <div className="mx-auto max-w-md">
                <h3 className="text-lg font-bold text-state-danger-light dark:text-state-danger-dark">
                  {t('list.loadError.title')}
                </h3>
                <p className="mt-2 text-sm text-state-danger-light dark:text-state-danger-dark">
                  {t('list.loadError.description')}
                </p>
                <Button className="mt-6 min-h-11" variant="outline" onClick={fetchConfigs}>
                  {t('list.loadError.retry')}
                </Button>
              </div>
            </Card>
          </div>
        ) : filteredConfigs.length === 0 ? (
          <Card className="py-24 text-center border-2 border-dashed">
            <div className="w-20 h-20 bg-surface-2-light dark:bg-surface-2-dark rounded-3xl flex items-center justify-center mx-auto mb-6 text-surface-border-light dark:text-surface-border-dark">
              <Settings size={40} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {configs.length > 0 && hasActiveFilters
                ? t('list.noResults.title')
                : t('list.empty.title')}
            </h3>
            <p className="text-text-muted-light dark:text-text-muted-dark mt-2 max-w-sm mx-auto font-medium text-sm">
              {configs.length > 0 && hasActiveFilters
                ? t('list.noResults.description')
                : t('list.empty.description')}
            </p>
            {configs.length > 0 && hasActiveFilters ? (
              <Button
                variant="outline"
                className="mt-8"
                onClick={() => {
                  setSearchQuery('');
                  setFilterType('all');
                  setFilterAgent('all');
                }}
              >
                {t('list.noResults.button')}
              </Button>
            ) : (
              <Button variant="outline" className="mt-8" onClick={() => setShowWizard(true)}>
                {t('list.empty.button')}
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredConfigs.map((config) => (
              <BackupConfigItem
                key={config.id}
                config={config}
                onEdit={() => {
                  navigate(`/backups/configs/${config.id}/edit`);
                }}
                onDelete={() => handleDelete(config)}
                onTrigger={() => handleTrigger(config)}
                onToggleEnable={() => handleToggleEnable(config)}
                onClick={() => {
                  navigate(`/backups/configs/${config.id}/snapshots`);
                }}
                onViewSnapshots={() => {
                  navigate(`/backups/configs/${config.id}/snapshots`);
                }}
              />
            ))}
          </div>
        )}
      </div>

      <BackupConfigWizard
        isOpen={showWizard}
        initialAgentId={filterAgent === 'all' ? '' : filterAgent}
        onClose={() => {
          setShowWizard(false);
          setEditConfigId(undefined);
          if (id) {
            navigate('/backups/configs');
          }
        }}
        onSuccess={() => {
          fetchConfigs();
          if (id) {
            navigate('/backups/configs');
          }
        }}
        editConfigId={editConfigId}
      />
    </>
  );
}
